// biome-ignore-start lint/style/noNonNullAssertion: parsing by accessing array at specific index
// biome-ignore-start lint/suspicious/noBitwiseOperators: parsing with bitwise operators
// biome-ignore-start lint/suspicious/noExplicitAny: parsing is hard

import {ScraperItem} from '@shared/models/parser';

export type CompressionFn = (data: Uint8Array) => Uint8Array;
export type DecompressionFn = (data: Uint8Array) => Uint8Array;

// Columnar, dictionary-based in-memory compression for lists of ScraperItem.
// Goal: minimize RAM while keeping random access reasonably fast.

export type CompressedArrayOfStrings = {
  flat: Int32Array; // concatenated string ids
  offsets: Int32Array; // size = N + 1, prefix sums into flat
};

export type CompressedScraperItems = {
  length: number;
  // numeric/scalar fields
  id: Int32Array;
  price: Int32Array; // -1 for missing (rounded to integer)
  areaM2: Int32Array; // -1 for missing (rounded to integer)
  landSurface: Int32Array; // -1 for missing (rounded to integer)
  nbRooms: Int16Array; // -1 missing
  nbBedrooms: Int16Array; // -1 missing
  floorNb: Int16Array; // -1 missing
  distributionType: Int16Array; // -1 missing
  idtiers: Int32Array; // -1 missing
  agencyId: Int32Array; // -1 missing
  parking: Uint8Array; // 0=missing, 1=false, 2=true

  // URL encoding
  // 0=missing, 1=fixedPattern (store middle only), 2=origin+suffix
  urlEncodingType: Uint8Array;
  // For fixedPattern
  urlFixedMiddle: (string | undefined)[];
  // For origin+suffix
  urlOriginId: Int32Array; // -1 if missing or not origin+suffix
  urlSuffix: (string | undefined)[]; // only for origin+suffix

  // Descriptions gzipped (UTF-8 -> gzip bytes)
  descriptionGzip: Uint8Array[]; // empty Uint8Array for missing

  // Dictionary-encoded string fields (indices into dictionary, -1 for missing)
  title: Int32Array;
  currency: Int32Array;
  city: Int32Array;
  estatePostalCode: Int32Array;
  clientPostalCode: Int32Array;
  country: Int32Array;
  zoneCode: Int32Array;
  provinceId: Int32Array;
  estateType: Int32Array;
  estateSubType: Int32Array;
  clientType: Int32Array;
  energyCertificate: Int32Array;
  promotionType: Int32Array;
  productType: Int32Array;
  typeLabel: Int32Array;
  locationLabel: Int32Array;

  // Images encoding (per-image)
  // 1=fixedPattern (store middle only), 2=origin+suffix
  imagesEncodingType: Uint8Array; // aligned with flat image list
  // For fixedPattern
  imagesFixedMiddles: string[]; // aligned with flat image list
  // For origin+suffix
  imagesOriginsFlat: Int32Array; // aligned with flat image list
  imagesSuffixes: string[]; // aligned with flat image list
  imagesOffsets: Int32Array; // size = N + 1

  productStatusTags: CompressedArrayOfStrings; // High repetition, use dictionary
  mediaTags: CompressedArrayOfStrings; // High repetition, use dictionary

  // Dictionary for fields with high repetition
  strings: string[]; // generic string dictionary for above fields
  urlOrigins: string[]; // dictionary of URL/image origins (scheme+host[:port])
};

type StringIndexer = {
  toId: (value: string) => number;
  values: string[];
};

function createStringIndexer(): StringIndexer {
  const map = new Map<string, number>();
  const values: string[] = [];
  return {
    toId: (value: string): number => {
      const existing = map.get(value);
      if (existing !== undefined) return existing;
      const id = values.length;
      values.push(value);
      map.set(value, id);
      return id;
    },
    values,
  };
}

function splitUrlIntoOriginAndSuffix(raw: string): {origin: string; suffix: string} {
  // Very lightweight split: origin = scheme://host[:port]
  // Find '://' then next '/' or '?' to end origin.
  const schemeIdx = raw.indexOf('://');
  if (schemeIdx === -1) {
    // No scheme, treat as whole suffix
    return {origin: '', suffix: raw};
  }
  const start = schemeIdx + 3;
  let end = raw.indexOf('/', start);
  const q = raw.indexOf('?', start);
  if (end === -1 || (q !== -1 && q < end)) {
    end = q;
  }
  if (end === -1) end = raw.length;
  const origin = raw.slice(0, end);
  const suffix = raw.slice(end);
  return {origin, suffix};
}

function writeStringIndexField(
  out: Int32Array,
  items: Array<ScraperItem | undefined>,
  getter: (i: ScraperItem) => string | undefined,
  indexer: StringIndexer
): void {
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it) {
      out[i] = -1;
      continue;
    }
    const v = getter(it);
    out[i] = v === undefined ? -1 : indexer.toId(v);
  }
}

function writeNumberFieldInt32(
  out: Int32Array,
  items: Array<ScraperItem | undefined>,
  getter: (i: ScraperItem) => number | undefined,
  roundToInt = false
): void {
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it) {
      out[i] = -1;
      continue;
    }
    const v = getter(it);
    if (v === undefined) {
      out[i] = -1;
    } else {
      out[i] = roundToInt ? Math.round(v) : v | 0;
    }
  }
}

function writeNumberFieldInt16(
  out: Int16Array,
  items: Array<ScraperItem | undefined>,
  getter: (i: ScraperItem) => number | undefined
): void {
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it) {
      out[i] = -1;
      continue;
    }
    const v = getter(it);
    out[i] = v === undefined ? -1 : v | 0;
  }
}

function writeBooleanTriState(
  out: Uint8Array,
  items: Array<ScraperItem | undefined>,
  getter: (i: ScraperItem) => boolean | undefined
): void {
  // 0=missing, 1=false, 2=true
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!it) {
      out[i] = 0;
      continue;
    }
    const v = getter(it);
    out[i] = v === undefined ? 0 : v ? 2 : 1;
  }
}

function writeArrayOfStringsField(
  items: Array<ScraperItem | undefined>,
  getter: (i: ScraperItem) => string[] | undefined,
  indexer: StringIndexer
): CompressedArrayOfStrings {
  const n = items.length;
  const lengths = new Int32Array(n);
  let total = 0;
  for (let i = 0; i < n; i++) {
    const it = items[i];
    const arr = it ? getter(it) : undefined;
    const len = arr?.length ?? 0;
    lengths[i] = len;
    total += len;
  }
  const flat = new Int32Array(total);
  const offsets = new Int32Array(n + 1);
  let cursor = 0;
  offsets[0] = 0;
  for (let i = 0; i < n; i++) {
    const it = items[i];
    const arr = it ? getter(it) : undefined;
    if (arr && arr.length > 0) {
      for (let j = 0; j < arr.length; j++) {
        flat[cursor++] = indexer.toId(arr[j]!);
      }
    }
    offsets[i + 1] = cursor;
  }
  return {flat, offsets};
}

export function compressScraperItems(items: ScraperItem[]): CompressedScraperItems {
  const n = items.length;
  const stringIndexer = createStringIndexer();
  const urlOriginsIndexer = createStringIndexer();
  // Known patterns
  const URL_FIXED_PREFIX = 'https://www.bellesdemeures.com/annonces/vente/tt-2-tb-';
  const URL_FIXED_SUFFIX = '&m=search_to_detail';
  const IMG_FIXED_PREFIX = 'https://v.seloger.com/s/';
  const IMG_FIXED_SUFFIX = '.jpg';
  let urlMismatchCount = 0;
  let imgMismatchCount = 0;

  // numeric/scalar
  const id = new Int32Array(n);
  for (let i = 0; i < n; i++) id[i] = items[i]!.id | 0;
  const price = new Int32Array(n);
  writeNumberFieldInt32(price, items, i => i.price, true); // Round to integer
  const areaM2 = new Int32Array(n);
  writeNumberFieldInt32(areaM2, items, i => i.areaM2, true); // Round to integer
  const landSurface = new Int32Array(n);
  writeNumberFieldInt32(landSurface, items, i => i.landSurface, true); // Round to integer
  const nbRooms = new Int16Array(n);
  writeNumberFieldInt16(nbRooms, items, i => i.nbRooms);
  const nbBedrooms = new Int16Array(n);
  writeNumberFieldInt16(nbBedrooms, items, i => i.nbBedrooms);
  const floorNb = new Int16Array(n);
  writeNumberFieldInt16(floorNb, items, i => i.floorNb);
  const distributionType = new Int16Array(n);
  writeNumberFieldInt16(distributionType, items, i => i.distributionType);
  const idtiers = new Int32Array(n);
  writeNumberFieldInt32(idtiers, items, i => i.idtiers);
  const agencyId = new Int32Array(n);
  writeNumberFieldInt32(agencyId, items, i => i.agencyId);
  const parking = new Uint8Array(n);
  writeBooleanTriState(parking, items, i => i.parking);

  // URL encoding
  const urlEncodingType = new Uint8Array(n); // 0 missing, 1 fixed, 2 origin+suffix
  const urlFixedMiddle: (string | undefined)[] = new Array(n);
  const urlOriginId = new Int32Array(n);
  const urlSuffix: (string | undefined)[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const u = items[i]?.url;
    if (u === undefined) {
      urlEncodingType[i] = 0;
      urlOriginId[i] = -1;
      urlSuffix[i] = undefined;
      urlFixedMiddle[i] = undefined;
    } else {
      if (u.startsWith(URL_FIXED_PREFIX) && u.endsWith(URL_FIXED_SUFFIX)) {
        urlEncodingType[i] = 1;
        urlFixedMiddle[i] = u.slice(URL_FIXED_PREFIX.length, u.length - URL_FIXED_SUFFIX.length);
        urlOriginId[i] = -1;
        urlSuffix[i] = undefined;
      } else {
        urlEncodingType[i] = 2;
        const {origin, suffix} = splitUrlIntoOriginAndSuffix(u);
        if (!origin || suffix.length === 0) {
          urlMismatchCount++;
        }
        urlOriginId[i] = origin ? urlOriginsIndexer.toId(origin) : -1;
        urlSuffix[i] = suffix;
        urlFixedMiddle[i] = undefined;
      }
    }
  }

  // Descriptions are already gzipped (Uint8Array), use directly
  const descriptionGzip: Uint8Array[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const d = items[i]?.description;
    if (d === undefined) {
      descriptionGzip[i] = new Uint8Array(0);
    } else {
      // Description is already gzipped, use it directly
      descriptionGzip[i] = d;
    }
  }

  // Dictionary-encoded string fields
  const title = new Int32Array(n);
  writeStringIndexField(title, items, i => i.title, stringIndexer);
  const currency = new Int32Array(n);
  writeStringIndexField(currency, items, i => i.currency, stringIndexer);
  const city = new Int32Array(n);
  writeStringIndexField(city, items, i => i.city, stringIndexer);
  const estatePostalCode = new Int32Array(n);
  writeStringIndexField(estatePostalCode, items, i => i.estatePostalCode, stringIndexer);
  const clientPostalCode = new Int32Array(n);
  writeStringIndexField(clientPostalCode, items, i => i.clientPostalCode, stringIndexer);
  const country = new Int32Array(n);
  writeStringIndexField(country, items, i => i.country, stringIndexer);
  const zoneCode = new Int32Array(n);
  writeStringIndexField(zoneCode, items, i => i.zoneCode, stringIndexer);
  const provinceId = new Int32Array(n);
  writeStringIndexField(provinceId, items, i => i.provinceId, stringIndexer);
  const estateType = new Int32Array(n);
  writeStringIndexField(estateType, items, i => i.estateType, stringIndexer);
  const estateSubType = new Int32Array(n);
  writeStringIndexField(estateSubType, items, i => i.estateSubType, stringIndexer);
  const clientType = new Int32Array(n);
  writeStringIndexField(clientType, items, i => i.clientType, stringIndexer);
  const energyCertificate = new Int32Array(n);
  writeStringIndexField(energyCertificate, items, i => i.energyCertificate, stringIndexer);
  const promotionType = new Int32Array(n);
  writeStringIndexField(promotionType, items, i => i.promotionType, stringIndexer);
  const productType = new Int32Array(n);
  writeStringIndexField(productType, items, i => i.productType, stringIndexer);
  const typeLabel = new Int32Array(n);
  writeStringIndexField(typeLabel, items, i => i.typeLabel, stringIndexer);
  const locationLabel = new Int32Array(n);
  writeStringIndexField(locationLabel, items, i => i.locationLabel, stringIndexer);

  // Images: prefer fixed pattern, else split into origin dictionary + suffix strings
  // First pass: count total images
  let totalImages = 0;
  for (let i = 0; i < n; i++) totalImages += items[i]!.images?.length ?? 0;
  const imagesEncodingType = new Uint8Array(totalImages);
  const imagesFixedMiddles: string[] = new Array(totalImages);
  const imagesOriginsFlat = new Int32Array(totalImages);
  const imagesSuffixes: string[] = new Array(totalImages);
  const imagesOffsets = new Int32Array(n + 1);
  let cursor = 0;
  imagesOffsets[0] = 0;
  for (let i = 0; i < n; i++) {
    const arr = items[i]?.images ?? [];
    for (let j = 0; j < arr.length; j++) {
      const raw = arr[j] as string;
      if (raw.startsWith(IMG_FIXED_PREFIX) && raw.endsWith(IMG_FIXED_SUFFIX)) {
        imagesEncodingType[cursor] = 1;
        imagesFixedMiddles[cursor] = raw.slice(
          IMG_FIXED_PREFIX.length,
          raw.length - IMG_FIXED_SUFFIX.length
        );
        imagesOriginsFlat[cursor] = -1;
        imagesSuffixes[cursor] = '';
      } else {
        imagesEncodingType[cursor] = 2;
        const {origin, suffix} = splitUrlIntoOriginAndSuffix(raw);
        if (!origin || suffix.length === 0) {
          imgMismatchCount++;
        }
        imagesOriginsFlat[cursor] = origin ? urlOriginsIndexer.toId(origin) : -1;
        imagesSuffixes[cursor] = suffix ?? '';
        imagesFixedMiddles[cursor] = '';
      }
      cursor++;
    }
    imagesOffsets[i + 1] = cursor;
  }
  if (urlMismatchCount > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `URL pattern mismatches: ${urlMismatchCount.toLocaleString()} (did not match fixed prefix/suffix)`
    );
  }
  if (imgMismatchCount > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `Image pattern mismatches: ${imgMismatchCount.toLocaleString()} (did not match fixed prefix/suffix)`
    );
  }
  const productStatusTags = writeArrayOfStringsField(
    items,
    i => i.productStatusTags,
    stringIndexer
  );
  const mediaTags = writeArrayOfStringsField(items, i => i.mediaTags, stringIndexer);

  return {
    length: n,
    id,
    price,
    areaM2,
    landSurface,
    nbRooms,
    nbBedrooms,
    floorNb,
    distributionType,
    idtiers,
    agencyId,
    parking,
    urlEncodingType,
    urlFixedMiddle,
    urlOriginId,
    urlSuffix,
    title,
    descriptionGzip,
    currency,
    city,
    estatePostalCode,
    clientPostalCode,
    country,
    zoneCode,
    provinceId,
    estateType,
    estateSubType,
    clientType,
    energyCertificate,
    promotionType,
    productType,
    typeLabel,
    locationLabel,
    imagesEncodingType,
    imagesFixedMiddles,
    imagesOriginsFlat,
    imagesSuffixes,
    imagesOffsets,
    productStatusTags,
    mediaTags,
    strings: stringIndexer.values,
    urlOrigins: urlOriginsIndexer.values,
  };
}

export function decompressScraperItemAt(c: CompressedScraperItems, index: number): ScraperItem {
  const anyC = c as unknown as Record<string, any>;
  const s = (id: number) => (id >= 0 ? c.strings[id] : undefined);
  const originStr = (id: number) => (id >= 0 ? c.urlOrigins[id] : '');
  const triToBool = (v: number) => (v === 0 ? undefined : v === 2);
  const rangeDictArray = (arr: CompressedArrayOfStrings) => {
    const start = arr.offsets[index]!;
    const end = arr.offsets[index + 1]!;
    const out: string[] = [];
    for (let i = start; i < end; i++) {
      out.push(c.strings[arr.flat[i] as number]!);
    }
    // Return empty array instead of undefined to match original data
    // (we can't distinguish between undefined and [] in compression, so default to [])
    return out;
  };
  const URL_FIXED_PREFIX = 'https://www.bellesdemeures.com/annonces/vente/tt-2-tb-';
  const URL_FIXED_SUFFIX = '&m=search_to_detail';
  const IMG_FIXED_PREFIX = 'https://v.seloger.com/s/';
  const IMG_FIXED_SUFFIX = '.jpg';
  // Build full images
  const imgStart = anyC['imagesOffsets'][index] as number;
  const imgEnd = anyC['imagesOffsets'][index + 1] as number;
  const images: string[] = [];
  for (let k = imgStart; k < imgEnd; k++) {
    const enc: number = anyC['imagesEncodingType'][k] ?? 0;
    if (enc === 1) {
      images.push(`${IMG_FIXED_PREFIX}${anyC['imagesFixedMiddles'][k]}${IMG_FIXED_SUFFIX}`);
    } else {
      images.push(`${originStr(anyC['imagesOriginsFlat'][k])}${anyC['imagesSuffixes'][k]}`);
    }
  }
  const imagesOut = images.length ? images : undefined;
  // Description is already gzipped, return as Uint8Array (lazy decompress on frontend)
  let description: Uint8Array | undefined;
  if (anyC['descriptionGzip'][index] && anyC['descriptionGzip'][index].byteLength > 0) {
    // Return gzipped description as-is (frontend will decompress lazily)
    description = anyC['descriptionGzip'][index] as Uint8Array;
  }
  // URL full
  let url: string | undefined;
  const urlEnc: number = anyC['urlEncodingType'][index] ?? 0;
  if (urlEnc === 1) {
    const mid = anyC['urlFixedMiddle'][index] as string | undefined;
    url = mid !== undefined ? `${URL_FIXED_PREFIX}${mid}${URL_FIXED_SUFFIX}` : undefined;
  } else if (urlEnc === 2) {
    const urlSuffix = anyC['urlSuffix'][index] as string | undefined;
    url =
      urlSuffix !== undefined ? `${originStr(anyC['urlOriginId'][index])}${urlSuffix}` : undefined;
  }
  return {
    id: anyC['id'][index] as number,
    url,
    title: s(anyC['title'][index] as number),
    description,
    price: anyC['price'][index] === -1 ? undefined : (anyC['price'][index] as number),
    currency: s(anyC['currency'][index] as number),
    city: s(anyC['city'][index] as number),
    estatePostalCode: s(anyC['estatePostalCode'][index] as number),
    clientPostalCode: s(anyC['clientPostalCode'][index] as number),
    country: s(anyC['country'][index] as number),
    zoneCode: s(anyC['zoneCode'][index] as number),
    provinceId: s(anyC['provinceId'][index] as number),
    areaM2: anyC['areaM2'][index] === -1 ? undefined : (anyC['areaM2'][index] as number),
    landSurface:
      anyC['landSurface'][index] === -1 ? undefined : (anyC['landSurface'][index] as number),
    nbRooms: anyC['nbRooms'][index] === -1 ? undefined : (anyC['nbRooms'][index] as number),
    nbBedrooms:
      anyC['nbBedrooms'][index] === -1 ? undefined : (anyC['nbBedrooms'][index] as number),
    floorNb: anyC['floorNb'][index] === -1 ? undefined : (anyC['floorNb'][index] as number),
    estateType: s(anyC['estateType'][index] as number),
    estateSubType: s(anyC['estateSubType'][index] as number),
    distributionType:
      anyC['distributionType'][index] === -1
        ? undefined
        : (anyC['distributionType'][index] as number),
    clientType: s(anyC['clientType'][index] as number),
    idtiers: anyC['idtiers'][index] === -1 ? undefined : (anyC['idtiers'][index] as number),
    agencyId: anyC['agencyId'][index] === -1 ? undefined : (anyC['agencyId'][index] as number),
    images: imagesOut,
    energyCertificate: s(anyC['energyCertificate'][index] as number),
    parking: triToBool(anyC['parking'][index] as number),
    productStatusTags: rangeDictArray(c.productStatusTags),
    promotionType: s(anyC['promotionType'][index] as number),
    productType: s(anyC['productType'][index] as number),
    mediaTags: rangeDictArray(c.mediaTags),
    typeLabel: s(anyC['typeLabel'][index] as number),
    locationLabel: s(anyC['locationLabel'][index] as number),
  };
}
// biome-ignore-end lint/style/noNonNullAssertion: parsing by accessing array at specific index
// biome-ignore-end lint/suspicious/noBitwiseOperators: parsing with bitwise operators
// biome-ignore-end lint/suspicious/noExplicitAny: parsing is hard
